// apps/client/src/pages/TrailForecastPage.tsx
// Trail Revenue Forecast Dashboard - Uses same data source as main Dashboard
// REWRITTEN (2025-12-03): Now uses useMonthlyAggregates for data consistency
// Added forecasting slider to project future growth and required new loans

import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import StatCard from '../components/common/StatCard';
import MonthlyBreakdownChart from '../components/charts/MonthlyBreakdownChart';
import { HandCoins, TrendingUp, Banknote, Activity, Target, Gauge, Handshake } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { formatCurrencyShorthand, formatNumber, formatCurrency } from '../utils/formatters';
import { useMonthlyAggregates } from '../hooks/useMonthlyAggregates';
import { useRolling3RunWindow } from '../hooks/useRolling3RunWindow';
import { useCompanyDataYearRange } from '../hooks/useCompanyDataYearRange';
import { resolveLoanBookSelection } from '../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import type { AppContextType } from '../components/layout/AppLayout';

const TrailForecastPage: React.FC = () => {
  const { userData, loanBooks = [] } = useOutletContext<AppContextType & { loanBooks?: ResolvedLoanBook[] }>();
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  // Filters matching Dashboard
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    yearType: 'calendar' as 'calendar' | 'financial',
  });

  // Growth slider for forecasting (1-25%)
  const [growthTarget, setGrowthTarget] = useState(5);

  // Revenue Model info tooltip
  const [showRevenueModelInfo, setShowRevenueModelInfo] = useState(false);

  // Generate year options for dropdown (newest first)
  // ✅ FIX (2026-01-05): Always include current year even if no data exists yet
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
    const years = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
      years.push(year);
    }
    return years;
  }, [earliestYear, latestYear]);

  // ============================================================================
  // USE SAME DATA SOURCE AS MAIN DASHBOARD
  // ============================================================================
  const {
    aggregates,
    loading: loadingAggregates,
    upfrontCommission,
    trailCommission,
  } = useMonthlyAggregates({
    companyId: userData?.companyId || '',
    year: filters.year,
    yearType: filters.yearType,
    entityCode: resolvedParams.entityCode,
  });

  // Get Line of Trail count from rolling 3 run
  const {
    totalLineOfTrail,
    loading: loadingRolling,
  } = useRolling3RunWindow({
    companyId: userData?.companyId || '',
    templateId: 'all',
    entityCode: resolvedParams.entityCode,
    yearMode: {
      year: filters.year,
      yearType: filters.yearType,
    },
  });

  // ============================================================================
  // CALCULATE MONTHLY CHART DATA (matching Dashboard pattern)
  // ============================================================================
  const monthlyChartData = useMemo(() => {
    const filterYear = parseInt(String(filters.year), 10);
    const monthsTemplate = Array.from({ length: 12 }, (_, i) => {
      const monthIndex = filters.yearType === 'financial' ? (i + 6) % 12 : i;
      const yearForMonth = filters.yearType === 'financial' && monthIndex < 6 ? filterYear + 1 : filterYear;
      return { year: yearForMonth, month: monthIndex + 1 };
    });

    return monthsTemplate.map(({ year, month }) => {
      const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
      const monthData = aggregates.find(agg => agg.yearMonth === yearMonth);

      return {
        month: new Date(year, month - 1).toLocaleString('default', { month: 'short' }),
        yearMonth,
        fullMonth: new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' }),
        // Convert cents to dollars for charts
        trail: monthData?.trailCommission ? monthData.trailCommission / 100 : 0,
        upfront: monthData?.upfrontCommission ? monthData.upfrontCommission / 100 : 0,
        trailCents: monthData?.trailCommission || 0,
        upfrontCents: monthData?.upfrontCommission || 0,
      };
    });
  }, [aggregates, filters.year, filters.yearType]);

  // ============================================================================
  // CALCULATE FORECASTS AND GROWTH METRICS
  // ============================================================================
  const forecastMetrics = useMemo(() => {
    // Get months with actual data
    const monthsWithData = monthlyChartData.filter(m => m.trail > 0);
    const monthCount = monthsWithData.length;

    if (monthCount === 0) {
      return {
        avgMonthlyTrail: 0,
        avgMonthlyUpfront: 0,
        projectedAnnualTrail: 0,
        projectedAnnualUpfront: 0,
        avgTrailPerLoan: 0,
        growthTargetTrail: 0,
        growthTargetUpfront: 0,
        loansNeededForGrowth: 0,
        currentMonthTrail: 0,
        trailPercentage: 0,
      };
    }

    // Calculate averages from actual data
    const avgMonthlyTrail = trailCommission / monthCount;
    const avgMonthlyUpfront = upfrontCommission / monthCount;

    // Annualize (extrapolate to 12 months)
    const projectedAnnualTrail = avgMonthlyTrail * 12;
    const projectedAnnualUpfront = avgMonthlyUpfront * 12;

    // Current month's trail (most recent with data)
    const currentMonthTrail = monthsWithData[monthsWithData.length - 1]?.trailCents || 0;

    // Average trail per loan
    const avgTrailPerLoan = totalLineOfTrail > 0 ? trailCommission / totalLineOfTrail : 0;

    // Growth target calculations
    const growthMultiplier = 1 + (growthTarget / 100);
    const growthTargetTrail = projectedAnnualTrail * growthMultiplier;
    const growthTargetUpfront = projectedAnnualUpfront * growthMultiplier;

    // Loans needed to hit growth target
    // New trail income needed = target - current
    const additionalTrailNeeded = growthTargetTrail - projectedAnnualTrail;
    const avgAnnualTrailPerLoan = avgTrailPerLoan * 12;
    const loansNeededForGrowth = avgAnnualTrailPerLoan > 0
      ? Math.ceil(additionalTrailNeeded / avgAnnualTrailPerLoan)
      : 0;

    // Trail as percentage of total revenue
    const totalRevenue = trailCommission + upfrontCommission;
    const trailPercentage = totalRevenue > 0 ? (trailCommission / totalRevenue) * 100 : 0;

    return {
      avgMonthlyTrail,
      avgMonthlyUpfront,
      projectedAnnualTrail,
      projectedAnnualUpfront,
      avgTrailPerLoan,
      growthTargetTrail,
      growthTargetUpfront,
      loansNeededForGrowth,
      currentMonthTrail,
      trailPercentage,
      monthCount,
    };
  }, [monthlyChartData, trailCommission, upfrontCommission, totalLineOfTrail, growthTarget]);

  // ============================================================================
  // CALCULATE MOM GROWTH RATES
  // ============================================================================
  const monthlyGrowthData = useMemo(() => {
    return monthlyChartData.map((current, idx) => {
      if (idx === 0 || current.trail === 0) {
        return { ...current, growthRate: null };
      }
      const previous = monthlyChartData[idx - 1];
      if (previous.trail === 0) {
        return { ...current, growthRate: null };
      }
      const growthRate = ((current.trail - previous.trail) / previous.trail) * 100;
      return { ...current, growthRate };
    });
  }, [monthlyChartData]);

  // Average growth rate (excluding nulls)
  const avgGrowthRate = useMemo(() => {
    const rates = monthlyGrowthData.filter(m => m.growthRate !== null).map(m => m.growthRate as number);
    return rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
  }, [monthlyGrowthData]);

  const loading = loadingAggregates || loadingRolling;

  if (loading) {
    return <div className="p-8 text-center text-white">Loading trail forecast...</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={TrendingUp}
        title="Trail Forecast"
        subtitle="Projected trail income based on historical trends"
      />

      <div>
      {/* Filter Controls */}
      <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-wrap gap-6 items-end">
          {/* Period Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Period Type</label>
            <div className="flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
              <button
                onClick={() => setFilters({ ...filters, yearType: 'calendar' })}
                className={`px-3 py-1 text-sm rounded-md ${
                  filters.yearType === 'calendar' ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                Calendar
              </button>
              <button
                onClick={() => setFilters({ ...filters, yearType: 'financial' })}
                className={`px-3 py-1 text-sm rounded-md ${
                  filters.yearType === 'financial' ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                Financial
              </button>
            </div>
          </div>

          {/* Year Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
              className="block bg-gray-700 border-gray-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {filters.yearType === 'financial' ? `${year}/${year + 1}` : year}
                </option>
              ))}
            </select>
          </div>

          {/* Loan Book Selector — unified-mode companies only */}
          {hasEntityLoanBooks && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Loan Book</label>
              <select
                value={selectedLoanBook}
                onChange={(e) => setSelectedLoanBook(e.target.value)}
                className="block bg-gray-700 border-gray-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Loan Books</option>
                {(loanBooks as ResolvedLoanBook[]).map((lb) => (
                  <option key={lb.id} value={lb.id}>{lb.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Growth Target Slider */}
          <div className="flex-1 min-w-[300px] max-w-[400px]">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Growth Target: <span className="text-green-400 font-bold">+{growthTarget}%</span>
            </label>
            <input
              type="range"
              min="1"
              max="25"
              step="1"
              value={growthTarget}
              onChange={(e) => setGrowthTarget(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
            />
            {/* Tick marks positioned using absolute positioning for exact alignment */}
            <div className="relative w-full h-4 mt-1">
              {[1, 5, 10, 15, 20, 25].map((tick) => (
                <span
                  key={tick}
                  className="absolute text-xs text-gray-500 -translate-x-1/2"
                  style={{ left: `${((tick - 1) / (25 - 1)) * 100}%` }}
                >
                  {tick}%
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards - Row 1: Current Metrics (matching Dashboard order & colors) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Upfront Commission"
          subtitle={filters.yearType === 'financial' ? `FY ${filters.year}/${filters.year + 1}` : `Year ${filters.year}`}
          value={formatCurrencyShorthand(upfrontCommission)}
          icon={<HandCoins size={24} />}
          gradientColor="#10b981"
        />
        <StatCard
          title="Trail Commission"
          subtitle={filters.yearType === 'financial' ? `FY ${filters.year}/${filters.year + 1}` : `Year ${filters.year}`}
          value={formatCurrencyShorthand(trailCommission)}
          icon={<TrendingUp size={24} />}
          gradientColor="#3b82f6"
        />
        <StatCard
          title="Avg Trail/Loan"
          subtitle="Monthly average"
          value={formatCurrencyShorthand(forecastMetrics.avgTrailPerLoan)}
          icon={<Banknote size={24} />}
          gradientColor="#f59e0b"
        />
        <StatCard
          title="Line of Trail"
          subtitle="Active trail loans"
          value={formatNumber(totalLineOfTrail)}
          icon={<Activity size={24} />}
          gradientColor="#8b5cf6"
        />
      </div>

      {/* Stat Cards - Row 2: Growth Targets (matching color scheme) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatCard
          title={`+${growthTarget}% Upfront Target`}
          subtitle="Projected with growth"
          value={formatCurrencyShorthand(forecastMetrics.growthTargetUpfront)}
          icon={<Target size={24} />}
          gradientColor="#10b981"
        />
        <StatCard
          title={`+${growthTarget}% Trail Target`}
          subtitle="Projected with growth"
          value={formatCurrencyShorthand(forecastMetrics.growthTargetTrail)}
          icon={<Target size={24} />}
          gradientColor="#3b82f6"
        />
        <StatCard
          title="New Loans Needed"
          subtitle={`To achieve +${growthTarget}% growth`}
          value={formatNumber(forecastMetrics.loansNeededForGrowth)}
          icon={<Handshake size={24} />}
          gradientColor="#f59e0b"
        />
        <StatCard
          title="Avg Growth Rate"
          subtitle="Month-over-month"
          value={avgGrowthRate !== null ? `${avgGrowthRate >= 0 ? '+' : ''}${avgGrowthRate.toFixed(2)}%` : 'N/A'}
          icon={<Gauge size={24} />}
          gradientColor="#8b5cf6"
        />
      </div>

      {/* Growth Planning Insights - Individual cards aligned with stat cards above */}
      <h3 className="font-bold text-lg mb-4">Growth Planning Insights</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-lg">
          <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">Trail as % of Revenue</p>
          <p className="text-xl md:text-2xl font-bold text-blue-400">
            {forecastMetrics.trailPercentage.toFixed(1)}%
          </p>
        </div>
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-lg">
          <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">Annualised Upfront</p>
          <p className="text-xl md:text-2xl font-bold text-green-400">
            {formatCurrencyShorthand(forecastMetrics.projectedAnnualUpfront)}
          </p>
        </div>
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-lg">
          <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">Annualised Trail</p>
          <p className="text-xl md:text-2xl font-bold text-purple-400">
            {formatCurrencyShorthand(forecastMetrics.projectedAnnualTrail)}
          </p>
        </div>
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-lg relative">
          <p className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2 flex items-center gap-2">
            Revenue Model
            <button
              onClick={() => setShowRevenueModelInfo(!showRevenueModelInfo)}
              className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 hover:bg-blue-500/40 hover:text-blue-300 transition-colors flex items-center justify-center text-xs font-bold"
              aria-label="What is Revenue Model?"
            >
              ?
            </button>
          </p>
          <p className="text-xl md:text-2xl font-bold text-white">
            {forecastMetrics.trailPercentage > 60
              ? 'Trail-Dominant'
              : forecastMetrics.trailPercentage > 40
              ? 'Balanced'
              : 'Upfront-Dominant'}
          </p>
          {/* Info tooltip styled like Valutten toast */}
          {showRevenueModelInfo && (
            <div className="absolute top-full left-0 mt-2 z-50 w-72">
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 shadow-2xl">
                <div className="px-4 py-3 flex items-start gap-3">
                  <div className="flex-shrink-0 text-white mt-0.5">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-white text-sm">
                    Shows whether your business relies more on <strong>upfront</strong> (transactional) or <strong>trail</strong> (recurring) income. Trail-dominant businesses have more predictable cash flow.
                  </div>
                  <button
                    onClick={() => setShowRevenueModelInfo(false)}
                    className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Charts - Use same MonthlyBreakdownChart as Dashboard (Upfront first, then Trail) */}
      <div className="grid grid-cols-1 gap-6">
        <MonthlyBreakdownChart
          data={monthlyChartData}
          title="Upfront Commission"
          dataKey="upfront"
          color="#10b981"
        />
        <MonthlyBreakdownChart
          data={monthlyChartData}
          title="Trail Commission"
          dataKey="trail"
          color="#3b82f6"
        />
      </div>

      {/* Monthly Breakdown Table - Reversed order (Dec at top, Jan at bottom) */}
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6">
        <h3 className="font-bold text-lg mb-4">Monthly Revenue Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-3 text-gray-400">Month</th>
                <th className="text-right p-3 text-gray-400">Trail Commission</th>
                <th className="text-right p-3 text-gray-400">Upfront Commission</th>
                <th className="text-right p-3 text-gray-400">Total</th>
                <th className="text-right p-3 text-gray-400">MoM Growth</th>
              </tr>
            </thead>
            <tbody>
              {/* Reverse the array so Dec is at top, Jan at bottom */}
              {[...monthlyGrowthData].reverse().map((monthData, idx) => (
                <tr key={idx} className="border-b border-gray-700 hover:bg-gray-750">
                  <td className="p-3 text-white font-medium">
                    {monthData.fullMonth}
                  </td>
                  <td className="p-3 text-right font-semibold text-blue-400">
                    {monthData.trail > 0 ? formatCurrency(monthData.trailCents) : '-'}
                  </td>
                  <td className="p-3 text-right font-semibold text-green-400">
                    {monthData.upfront > 0 ? formatCurrency(monthData.upfrontCents) : '-'}
                  </td>
                  <td className="p-3 text-right font-semibold text-white">
                    {(monthData.trail > 0 || monthData.upfront > 0)
                      ? formatCurrency(monthData.trailCents + monthData.upfrontCents)
                      : '-'}
                  </td>
                  <td
                    className={`p-3 text-right font-semibold ${
                      monthData.growthRate === null
                        ? 'text-gray-500'
                        : monthData.growthRate >= 0
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}
                  >
                    {monthData.growthRate === null
                      ? '-'
                      : `${monthData.growthRate >= 0 ? '+' : ''}${monthData.growthRate.toFixed(2)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Growth Planning Methodology */}
      <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">Growth Planning Methodology</h3>
        <p className="text-sm text-gray-400">
          This forecast uses the same data as the main Dashboard for consistency.
          {forecastMetrics.monthCount && forecastMetrics.monthCount < 12 && (
            <> Annualised figures are extrapolated from {forecastMetrics.monthCount} month{forecastMetrics.monthCount !== 1 ? 's' : ''} of actual data.</>
          )}
          {' '}The "New Loans Needed" calculation estimates how many additional loans you need to settle
          to achieve your {growthTarget}% growth target, based on your current average trail per loan
          of {formatCurrencyShorthand(forecastMetrics.avgTrailPerLoan)}/month.
        </p>
      </div>

      {/* No data message */}
      {aggregates.length === 0 && (
        <div className="mt-8 p-4 bg-gray-800 rounded-lg text-center">
          <p className="text-gray-400">
            No commission data for the selected period. Upload commission files to see trail revenue forecasts.
          </p>
        </div>
      )}
      </div>
    </div>
  );
};

export default TrailForecastPage;
